import * as React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Menu,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';
import Form from './Form';
import Show from './Show';
import DeleteConfirmation from '../../components/DeleteConfirmation';
import UserService from '../../../services/user.service';

const headCells = [
  {
    id: 'name',
    numeric: false,
    label: 'Nom',
  },
  {
    id: 'email',
    numeric: false,
    label: 'Email',
  },
  {
    id: 'pin',
    numeric: false,
    label: 'PIN',
  },
  {
    id: 'roles',
    numeric: false,
    label: 'Rôles',
  },
  {
    id: 'actions',
    numeric: false,
    label: '',
  },
];

function getRoleLabel(role) {
  switch (role) {
    case 'ROLE_ADMIN':
      return { label: 'Administrateur', color: 'error' };
    case 'ROLE_HOTESS':
      return { label: 'Hôtesse', color: 'primary' };
    case 'ROLE_SELLER':
      return { label: 'Caissier', color: 'success' };
    default:
      return { label: role, color: 'default' };
  }
}

// Je réutilise les mêmes fonctions de tri que dans le composant des tentes
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding='normal'
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function ActionMenu({ row, onEditClick, onShowClick, onDeleteClick }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleEdit = (event) => {
    handleClose(event);
    onEditClick(row);
  };

  const handleShow = (event) => {
    handleClose(event);
    onShowClick(row);
  };

  const handleDeleteClick = (event) => {
    handleClose(event);
    setDeleteConfirmOpen(true);
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={handleShow} sx={{ color: 'primary.main', gap: 1 }}>
          <InfoIcon /> <span>Détails</span>
        </MenuItem>
        <MenuItem onClick={handleEdit} sx={{ color: 'orange', gap: 1 }}>
          <EditIcon /> <span>Modifier</span>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'red', gap: 1 }}>
          <DeleteIcon /> <span>Supprimer</span>
        </MenuItem>
      </Menu>

      <DeleteConfirmation
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={() => {
          onDeleteClick(row);
          setDeleteConfirmOpen(false);
        }}
        title="Supprimer l'utilisateur"
        content="Êtes-vous sûr de vouloir supprimer cet utilisateur ?"
      />
    </>
  );
}

export default function UserList() {
  const [users, setUsers] = React.useState([]);
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('name');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [isShowOpen, setIsShowOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success'
  });

  React.useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await UserService.list();
      setUsers(response);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des utilisateurs',
        severity: 'error'
      });
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const visibleRows = React.useMemo(
    () => filteredUsers
      .sort(getComparator(order, orderBy))
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, filteredUsers]
  );

  return (
    <Box sx={{ width: '100%', backgroundColor: '#f8f9fa' }}>
      <Paper sx={{ width: '100%', mb: 2, borderRadius: '12px' }}>
        <Toolbar sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          justifyContent: 'space-between',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Utilisateurs
          </Typography>
          <div className="d-flex align-items-center gap-3">
            <TextField
              size="small"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedItem(null);
                setIsFormOpen(true);
              }}
            >
              Ajouter
            </Button>
          </div>
        </Toolbar>

        <TableContainer>
          <Table>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.pin}</TableCell>
                  <TableCell>
                    {row.roles.map(role => {
                      const { label, color } = getRoleLabel(role);
                      return (
                        <Chip
                          key={role}
                          label={label}
                          color={color}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      );
                    })}
                  </TableCell>
                  <TableCell align="right">
                    <ActionMenu
                      row={row}
                      onEditClick={(user) => {
                        setSelectedItem(user);
                        setIsFormOpen(true);
                      }}
                      onShowClick={(user) => {
                        setSelectedItem(user);
                        setIsShowOpen(true);
                      }}
                      onDeleteClick={async (user) => {
                        try {
                          await UserService.delete(user.id);
                          await fetchUsers();
                          setSnackbar({
                            open: true,
                            message: 'Utilisateur supprimé avec succès',
                            severity: 'success'
                          });
                        } catch (error) {
                          setSnackbar({
                            open: true,
                            message: 'Erreur lors de la suppression',
                            severity: 'error'
                          });
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Form
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedItem(null);
        }}
        initialData={selectedItem}
        onSubmit={async (formData, onError) => {
          try {
            if (selectedItem) {
              await UserService.update(formData);
              setSnackbar({
                open: true,
                message: 'Utilisateur modifié avec succès',
                severity: 'success'
              });
            } else {
              await UserService.create(formData);
              setSnackbar({
                open: true,
                message: 'Utilisateur créé avec succès',
                severity: 'success'
              });
            }
            await fetchUsers();
            setIsFormOpen(false);
            setSelectedItem(null);
          } catch (error) {
            if (error.validationErrors && error.validationErrors.length > 0 && onError) {
              onError(error.validationErrors);
            } else {
              setSnackbar({
                open: true,
                message: error.message || 'Erreur lors de l\'enregistrement',
                severity: 'error'
              });
            }
          }
        }}
      />

      <Show
        open={isShowOpen}
        onClose={() => {
          setIsShowOpen(false);
          setSelectedItem(null);
        }}
        userData={selectedItem}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 